import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { TrendingUp, Download } from 'lucide-react'

interface AnalyticsData {
  dailyVolume: Array<{ date: string; volume: number; trades: number }>
  projectDistribution: Array<{ name: string; value: number }>
  userGrowth: Array<{ date: string; users: number }>
  topProjects: Array<{ name: string; volume: number; trades: number }>
}

export default function AnalyticsPage() {
  const [data, setData] = useState<AnalyticsData>({
    dailyVolume: [],
    projectDistribution: [],
    userGrowth: [],
    topProjects: [],
  })
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d')
  const [totalStats, setTotalStats] = useState({
    totalVolume: 0,
    totalTrades: 0,
    totalRevenue: 0,
    avgTradeSize: 0,
  })

  useEffect(() => {
    fetchAnalytics()
  }, [timeRange])

  const fetchAnalytics = async () => {
    try {
      // Calculate date range
      const daysAgo = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - daysAgo)

      // Fetch trades for the period
      const { data: trades, error } = await supabase
        .from('trades')
        .select('*')
        .gte('executed_at', startDate.toISOString())
        .order('executed_at', { ascending: true })

      if (error) throw error

      // Process data for charts
      if (trades && trades.length > 0) {
        // Daily volume aggregation
        const volumeByDay = new Map<string, { volume: number; trades: number }>()
        trades.forEach((trade: any) => {
          const date = new Date(trade.executed_at).toLocaleDateString()
          const existing = volumeByDay.get(date) || { volume: 0, trades: 0 }
          volumeByDay.set(date, {
            volume: existing.volume + trade.quantity_tons,
            trades: existing.trades + 1,
          })
        })

        const dailyVolume = Array.from(volumeByDay.entries()).map(([date, data]) => ({
          date,
          volume: data.volume,
          trades: data.trades,
        }))

        // Project distribution
        const volumeByProject = new Map<string, number>()
        trades.forEach((trade: any) => {
          const existing = volumeByProject.get(trade.project_id) || 0
          volumeByProject.set(trade.project_id, existing + trade.quantity_tons)
        })

        const projectDistribution = Array.from(volumeByProject.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([name, value]) => ({
            name: name.slice(0, 8) + '...',
            value,
          }))

        // Top projects
        const projectStats = new Map<string, { volume: number; trades: number }>()
        trades.forEach((trade: any) => {
          const existing = projectStats.get(trade.project_id) || { volume: 0, trades: 0 }
          projectStats.set(trade.project_id, {
            volume: existing.volume + trade.quantity_tons,
            trades: existing.trades + 1,
          })
        })

        const topProjects = Array.from(projectStats.entries())
          .sort((a, b) => b[1].volume - a[1].volume)
          .slice(0, 5)
          .map(([name, stats]) => ({
            name: name.slice(0, 12) + '...',
            volume: stats.volume,
            trades: stats.trades,
          }))

        // Calculate total stats
        const totalVolume = trades.reduce((sum: number, t: any) => sum + t.quantity_tons, 0)
        const totalRevenue = trades.reduce(
          (sum: number, t: any) => sum + t.buyer_fee + t.seller_fee,
          0
        )

        setData({
          dailyVolume,
          projectDistribution,
          userGrowth: [], // Would need historical user data
          topProjects,
        })

        setTotalStats({
          totalVolume,
          totalTrades: trades.length,
          totalRevenue,
          avgTradeSize: totalVolume / trades.length,
        })
      }
    } catch (error) {
      console.error('Error fetching analytics:', error)
    } finally {
      setLoading(false)
    }
  }

  const exportData = () => {
    // Create CSV
    const headers = ['Date', 'Volume (tons)', 'Trades']
    const rows = data.dailyVolume.map((d) => [d.date, d.volume, d.trades])
    const csv = [headers, ...rows].map((row) => row.join(',')).join('\n')

    // Download
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `xtan-analytics-${timeRange}.csv`
    a.click()
  }

  const COLORS = ['#1e40af', '#3b82f6', '#60a5fa', '#93c5fd', '#dbeafe']

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-admin-primary"></div>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics & Reports</h1>
          <p className="text-gray-600 mt-1">Platform performance metrics</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-admin-primary focus:border-admin-primary"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <button onClick={exportData} className="admin-button-secondary flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="admin-card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Total Volume</p>
            <TrendingUp className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {totalStats.totalVolume.toLocaleString()}
          </p>
          <p className="text-sm text-gray-600 mt-1">tons CO2</p>
        </div>

        <div className="admin-card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Total Trades</p>
            <TrendingUp className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {totalStats.totalTrades.toLocaleString()}
          </p>
          <p className="text-sm text-gray-600 mt-1">completed</p>
        </div>

        <div className="admin-card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Total Revenue</p>
            <TrendingUp className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">
            ${totalStats.totalRevenue.toLocaleString()}
          </p>
          <p className="text-sm text-gray-600 mt-1">in fees</p>
        </div>

        <div className="admin-card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Avg Trade Size</p>
            <TrendingUp className="w-5 h-5 text-orange-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {totalStats.avgTradeSize.toFixed(1)}
          </p>
          <p className="text-sm text-gray-600 mt-1">tons per trade</p>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Daily Volume */}
        <div className="admin-card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Trading Volume</h3>
          {data.dailyVolume.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.dailyVolume}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="volume" fill="#1e40af" name="Volume (tons)" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-300 flex items-center justify-center text-gray-500">
              No data available
            </div>
          )}
        </div>

        {/* Project Distribution */}
        <div className="admin-card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Volume by Project (Top 5)
          </h3>
          {data.projectDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data.projectDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(data) => `${data.name}: ${data.value}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {data.projectDistribution.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-300 flex items-center justify-center text-gray-500">
              No data available
            </div>
          )}
        </div>
      </div>

      {/* Top Projects Table */}
      <div className="admin-card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Trading Projects</h3>
        {data.topProjects.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Project ID</th>
                  <th>Total Volume</th>
                  <th>Total Trades</th>
                  <th>Avg Trade Size</th>
                </tr>
              </thead>
              <tbody>
                {data.topProjects.map((project, index) => (
                  <tr key={project.name}>
                    <td className="font-bold">#{index + 1}</td>
                    <td className="font-mono text-sm">{project.name}</td>
                    <td className="font-medium">{project.volume.toLocaleString()} tons</td>
                    <td>{project.trades}</td>
                    <td>{(project.volume / project.trades).toFixed(1)} tons</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">No trading data available</div>
        )}
      </div>
    </div>
  )
}
